/**
 * =============================================================================
 * STOX Theme - Product Page JavaScript
 * =============================================================================
 * 
 * This file contains product page specific behaviors.
 * 
 * DEPENDENCIES:
 * - index.js (window.app, window.component)
 * - base.js (window.Stox.addToCart, window.Stox.onReady, window.Stox.bindOnce)
 * 
 * COMPONENTS:
 * - productSelection: Variant option selection
 * - productForm: Add to cart form handling
 * - mainProduct: Stock status, quantity, size selector toggle
 * - productBackInStock: Back in stock notification modal
 * - sizeSelector: Size selection drawer
 * - genderedSelector / unisexSelector: Size wizard selectors
 * =============================================================================
 */

(() => {
  "use strict";

  // ==========================================================================
  // EARLY EXIT: Only run on product pages
  // ==========================================================================
  
  const productJsonEl = document.querySelector(".js-product-json");
  if (!productJsonEl) return; // Not a product page, exit early

  // ==========================================================================
  // Restore scroll position after PDP color change (full page navigation)
  // ==========================================================================

  const PDP_COLOR_SCROLL_KEY = "stox-pdp-color-scroll-y";

  (function restoreScrollAfterColorChange() {
    const saved = sessionStorage.getItem(PDP_COLOR_SCROLL_KEY);
    if (saved === null) return;

    sessionStorage.removeItem(PDP_COLOR_SCROLL_KEY);
    const scrollY = Number.parseInt(saved, 10);
    if (!Number.isFinite(scrollY) || scrollY < 0) return;

    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }

    const restore = () => window.scrollTo(0, scrollY);

    restore();
    document.addEventListener("DOMContentLoaded", restore, { once: true });
    window.addEventListener("load", restore, { once: true });

    const onReady = window.Stox?.onReady || ((fn) => {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", fn, { once: true });
      } else {
        fn();
      }
    });
    onReady(restore);
  })();

  const saveScrollBeforeColorChange = () => {
    sessionStorage.setItem(PDP_COLOR_SCROLL_KEY, String(window.scrollY));
  };

  // ==========================================================================
  // DOM ELEMENT FINDER UTILITY (same as base.js)
  // ==========================================================================

  function e(e = document.body, t = "js-") {
    const n = [...e.querySelectorAll(t ? `[class*="${t}"]` : "*")],
      i = t ? "classList" : "dataset";
    return n.reduce(
      (e, n) => (
        [].slice.call(t ? n[i] : Object.entries(n[i])).forEach((i) => {
          let a;
          t && i.slice(0, t.length) === t
            ? (a = i.slice(t.length, i.length))
            : t || ([a] = i),
            a &&
              (e[a] = e.hasOwnProperty(a)
                ? e[a].constructor === Array
                  ? e[a].concat(n)
                  : [e[a], n]
                : n);
        }),
        e
      ),
      {}
    );
  }

  // ==========================================================================
  // MONEY FORMATTING
  // ==========================================================================

  function formatMoney(amount, format = window.Stox?.money_format) {
    if (typeof amount === "string") {
      amount = parseFloat(amount.replace(",", ".").replace(/[^\d.-]/g, ""));
    }

    if (isNaN(amount) || amount == null) return "";

    const match = format?.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = match ? match[0] : "{{amount}}";
    const formatType = match ? match[1] : "amount";

    const useComma = formatType.includes("comma") || window.Stox?.money_separator === ",";
    const decimalSeparator = useComma ? "," : ".";
    const thousandsSeparator = useComma ? "." : ",";
    const decimalPlaces = formatType.includes("no_decimals") ? 0 : 2;

    const value = (amount / 100).toFixed(decimalPlaces);
    const [intPart, decimalPart = ""] = value.split(".");
    const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    const formatted = decimalPlaces > 0 
      ? `${intFormatted}${decimalSeparator}${decimalPart}` 
      : intFormatted;

    return format?.replace(placeholder, formatted) || formatted;
  }

  // ==========================================================================
  // TRANSLATION HELPER
  // ==========================================================================

  const translate = (key) => {
    return key.split(".").reduce((obj, k) => obj?.[k], window.Stox?.theme?.strings) 
      || `Missing translation for ${key}`;
  };

  // ==========================================================================
  // PRICE UPDATE HELPER
  // ==========================================================================

  const updatePrice = (variant, container) => {
    const { price: priceEl, compareAtPrice: comparePriceEl } = e(container, null);
    if (!priceEl) return;

    const { price, compare_at_price } = variant;

    priceEl.innerHTML = formatMoney(price);

    if (compare_at_price > price && !window.isBundle) {
      priceEl.classList.add("text-red-discount");
      if (comparePriceEl) comparePriceEl.innerHTML = formatMoney(compare_at_price);
    } else {
      priceEl.classList.remove("text-red-discount");
      if (comparePriceEl) comparePriceEl.innerHTML = "";
    }
  };

  // ==========================================================================
  // SHARED STATE
  // ==========================================================================

  const isCartPage = window.location.href.includes(window.Stox?.routes?.cart || '/cart');
  let currentVariant = {};
  let selectedVariant = {};

  // Helper to get current variant (used by size selectors)
  const getCurrentVariant = () => currentVariant;

  // ==========================================================================
  // COMPONENT DEFINITIONS
  // ==========================================================================

  const productSelection = window.component((container, app) => {
    // Variant selector - handles both select and radio inputs
    const createVariantSelector = () => {
      const selectors = {
        select: "[data-option-select]",
        radio: "[data-option-radio]",
        main: "[data-option-main]",
      };

      const callbacks = [];
      const state = { id: null, options: [] };

      const selectEls = document.querySelectorAll(selectors.select);
      const radioEls = document.querySelectorAll(selectors.radio);
      const mainEl = document.querySelector(selectors.main);

      if (!mainEl) {
        console.warn("productSelection: [data-option-main] element not found");
        return { state: { id: null, options: [] }, onUpdate: () => () => {} };
      }

      // Build variant lookup from main select options
      const variantLookup = Array.from(mainEl.children).reduce((acc, child) => {
        acc[child.innerHTML] = child.value;
        return acc;
      }, {});

      const updateVariant = () => {
        state.id = variantLookup[state.options.join(" / ")];
        mainEl.value = state.id;
        callbacks.forEach((cb) => cb(state));
      };

      // Setup select inputs
      selectEls.forEach((select) => {
        if (select.nodeName !== "SELECT") return;
        
        const index = Number(select.getAttribute("data-index"));
        state.options[index] = select.value;

        select.addEventListener("change", (ev) => {
          state.options[index] = ev.target.value;
          updateVariant();
        });
      });

      // Setup radio inputs
      radioEls.forEach((radioGroup) => {
        if (radioGroup.nodeName === "INPUT") return;
        
        const index = Number(radioGroup.getAttribute("data-index"));
        const radios = Array.from(radioGroup.getElementsByTagName("input"));

        radios.forEach((radio) => {
          if (radio.checked) state.options[index] = radio.value;
          
          radio.onclick = (ev) => {
            state.options[index] = ev.target.value;
            updateVariant();
          };
        });
      });

      updateVariant();
      
      // Check for ?size= parameter and pre-select that size (for cross-gender recommendations)
      const urlParams = new URLSearchParams(window.location.search);
      const sizeParam = urlParams.get('size');
      if (sizeParam) {
        // Try to find and select the size in radio inputs
        radioEls.forEach((radioGroup) => {
          const radios = Array.from(radioGroup.getElementsByTagName("input"));
          radios.forEach((radio) => {
            if (radio.value === sizeParam) {
              radio.checked = true;
              radio.click();
            }
          });
        });
        
        // Try to find and select the size in select inputs
        selectEls.forEach((select) => {
          Array.from(select.options).forEach((option) => {
            if (option.value === sizeParam) {
              select.value = sizeParam;
              select.dispatchEvent(new Event('change'));
            }
          });
        });
        
        // Clean up URL (remove size param)
        urlParams.delete('size');
        const newUrl = urlParams.toString() 
          ? `${window.location.pathname}?${urlParams.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }

      return {
        get state() { return state; },
        onUpdate: (cb) => {
          if (callbacks.indexOf(cb) < 0) callbacks.push(cb);
          return () => callbacks.splice(callbacks.indexOf(cb), 1);
        },
      };
    };

    const variantSelector = createVariantSelector();
    
    let productData;
    try {
      productData = JSON.parse(productJsonEl.innerHTML);
    } catch (err) {
      console.error("Failed to parse product JSON:", err);
      return;
    }

    const { product: { variants }, selectedOrFirstAvailableVariant } = productData;
    
    // Initialize with selected variant
    let activeVariant = variants.find((v) => v.id === selectedOrFirstAvailableVariant) 
      || variants.find((v) => v?.available) 
      || variants[0];

    app.emit("variant:change", null, activeVariant);

    variantSelector.onUpdate(({ id }) => {
      activeVariant = variants.find((v) => v.id === parseInt(id, 10));
      if (!activeVariant) return;

      updatePrice(activeVariant, container);

      // Update URL with variant
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);
      params.set("variant", encodeURIComponent(id));
      window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);

      app.emit("variant:change", null, activeVariant);
    });
  });

  const productForm = window.component((form, app) => {
    const {
      submit: submitBtn,
      submitText,
      compareAtPrice,
      price,
      backInStockModal,
    } = e(form, null);

    if (!submitBtn) return; // Defensive check

    let productData;
    try {
      productData = JSON.parse(productJsonEl.innerHTML);
    } catch (err) {
      console.error("Failed to parse product JSON:", err);
      return;
    }

    const { selectedOrFirstAvailableVariant } = productData;
    selectedVariant = selectedOrFirstAvailableVariant;

    app.on("variant:change", (state, variant) => {
      selectedVariant = variant;
    });

    form.addEventListener("submit", (ev) => {
      ev.preventDefault();

      if (selectedVariant?.available) {
        // Add to cart flow
        submitBtn.disabled = true;
        if (submitText) submitText.textContent = translate("product.adding_to_cart");
        if (compareAtPrice) compareAtPrice.textContent = "";
        if (price) price.textContent = "";

        const quantity = form.elements?.quantity?.value || 1;

        // Use shared cart function from base.js
        window.Stox.addToCart([{ id: selectedVariant.id, quantity: parseInt(quantity, 10) }])
          .then(({ error }) => {
            if (error) alert(error);

            submitBtn.disabled = true;
            if (submitText) submitText.textContent = translate("product.added_to_cart");

            setTimeout(() => {
              if (!error) app.emit("cart:toggle", { cartOpen: true });
            }, 0);

            setTimeout(() => {
              submitBtn.disabled = false;
              if (submitText) submitText.innerHTML = translate("product.add_to_cart");
              updatePrice(selectedVariant, submitBtn);
            }, error ? 0 : 1000);
          });
      } else {
        // Show back in stock modal (not for UK market)
        const isUK = window.Stox?.country === 'GB';
        if (isUK) return; // UK doesn't have Klaviyo notify, button should be disabled
        
        (async () => {
          if (!backInStockModal) return;
          
          const html = await fetch(
            `${window.location.origin}${window.location.pathname}?section_id=product-back-in-stock`
          ).then((res) => res.text());

          backInStockModal.innerHTML = html;
          window.app?.unmount?.();
          window.app?.mount?.();
        })();
      }
    });
  });

  const mainProduct = window.component(async (container, app) => {
    const {
      sizePlaceholder,
      adviceVariantSize,
      adviceShoeSize,
      adviceAnkleGirth,
      adviceCalfWidth,
      adviceWrist,
      adviceUpperArm,
      stock,
      stockLight,
      quantity,
      quantityMemory,
      decrementQuantity,
      incrementQuantity,
      submit: submitBtn,
      submitText,
      sizeRadio,
      sizeSelectorToggle,
    } = e(container, null);

    app.on("variant:change", (state, variant) => {
      currentVariant = variant;

      // Get size from appropriate option
      const currentSize = variant?.option2 || variant?.option1;
      const sizeData = window.sizes?.[currentSize];

      // Update size radio selection
      if (sizeRadio) {
        [].concat(sizeRadio).forEach((radio) => {
          if (radio.value === currentSize) radio.checked = true;
        });
      }

      // Update size display elements
      if (sizePlaceholder) sizePlaceholder.innerHTML = currentSize || "";
      if (adviceVariantSize) adviceVariantSize.innerHTML = currentSize || "";

      // Update size advice data
      if (sizeData) {
        const location = window.sizes?.location;
        if (adviceShoeSize) adviceShoeSize.innerHTML = sizeData[`${location}ShoeSize`] || "";
        if (adviceAnkleGirth) adviceAnkleGirth.innerHTML = sizeData[location === "EU" ? "metricAnkleGirth" : "imperialAnkle"] || "";
        if (adviceCalfWidth) adviceCalfWidth.innerHTML = sizeData[location === "EU" ? "metricCalfWidth" : "imperialCalf"] || "";
        if (adviceWrist) adviceWrist.innerHTML = sizeData[location === "EU" ? "metricWrist" : "imperialWrist"] || "";
        if (adviceUpperArm) adviceUpperArm.innerHTML = sizeData[location === "EU" ? "metricUpperArm" : "imperialUpperArm"] || "";
      } else {
        if (adviceShoeSize) adviceShoeSize.innerHTML = "";
        if (adviceAnkleGirth) adviceAnkleGirth.innerHTML = "";
        if (adviceCalfWidth) adviceCalfWidth.innerHTML = "";
        if (adviceWrist) adviceWrist.innerHTML = "";
        if (adviceUpperArm) adviceUpperArm.innerHTML = "";
      }

      // Update stock status and button state
      if (stock && stockLight && submitBtn && submitText) {
        const { available, inventory_quantities, discontinuing_product } = variant;
        const isUK = window.Stox?.country === 'GB';

        if (available && inventory_quantities > 0) {
          // In stock
          stockLight.classList.remove("bg-red", "bg-orange");
          stockLight.classList.add("bg-green");
          stock.innerHTML = translate("product.in_stock");
          submitBtn.classList.remove("bg-grey-800", "bg-white", "text-black", "border", "border-black");
          submitBtn.classList.add("bg-black", "text-white");
          submitBtn.disabled = false;
          submitText.innerHTML = translate("product.add_to_cart");
        } else if (discontinuing_product && inventory_quantities < 1) {
          // Discontinued
          stockLight.classList.remove("bg-orange", "bg-green");
          stockLight.classList.add("bg-red");
          stock.innerHTML = translate("product.out_of_stock_text");
          submitBtn.classList.remove("bg-black", "bg-white", "text-black", "border", "border-black");
          submitBtn.classList.add("bg-grey-800", "text-white");
          submitBtn.disabled = true;
          submitText.innerHTML = translate("product.unavailable");
        } else if (isUK && inventory_quantities < 1) {
          // Out of stock - UK market (no Klaviyo, no pre-order, just sold out)
          stockLight.classList.remove("bg-orange", "bg-green");
          stockLight.classList.add("bg-red");
          stock.innerHTML = translate("product.out_of_stock_text");
          submitBtn.classList.remove("bg-black", "bg-white", "text-black", "border", "border-black");
          submitBtn.classList.add("bg-grey-800", "text-white");
          submitBtn.disabled = true;
          submitText.innerHTML = translate("product.unavailable");
        } else if (available && inventory_quantities < 1) {
          // Pre-order (available but no stock - only for non-UK markets)
          stockLight.classList.remove("bg-green", "bg-red");
          stockLight.classList.add("bg-orange");
          stock.innerHTML = translate("product.pre_order_text");
          submitBtn.classList.remove("bg-grey-800", "bg-white", "text-black", "border", "border-black");
          submitBtn.classList.add("bg-black", "text-white");
          submitBtn.disabled = false;
          submitText.innerHTML = translate("product.pre_order");
        } else {
          // Out of stock - other markets (can notify via Klaviyo)
          stockLight.classList.remove("bg-green", "bg-orange");
          stockLight.classList.add("bg-red");
          stock.innerHTML = translate("product.out_of_stock_text");
          submitBtn.classList.remove("bg-black", "bg-grey-800", "text-white");
          submitBtn.classList.add("bg-white", "text-black", "border", "border-black");
          submitBtn.disabled = false;
          submitText.innerHTML = translate("product.notify_me_cta");
        }
      }
    });

    // Quantity controls
    if (quantity && quantityMemory) {
      quantity.innerHTML = quantityMemory.value;

      if (decrementQuantity) {
        decrementQuantity.addEventListener("click", (ev) => {
          ev.preventDefault();
          if (parseInt(quantityMemory.value, 10) > 1) {
            quantityMemory.value--;
            quantity.innerHTML = quantityMemory.value;
          }
        });
      }

      if (incrementQuantity) {
        incrementQuantity.addEventListener("click", (ev) => {
          ev.preventDefault();
          quantityMemory.value++;
          quantity.innerHTML = quantityMemory.value;
        });
      }
    }

    // Size selector toggle
    if (sizeSelectorToggle) {
      sizeSelectorToggle.addEventListener("click", (ev) => {
        ev.preventDefault();
        app.emit("sizeSelector:toggle", ({ sizeSelectorOpen }) => ({ 
          sizeSelectorOpen: !sizeSelectorOpen ?? true 
        }));
      });
    }
  });

  const productBackInStock = window.component(async (modal, app) => {
    const { close: closeBtn, notify: notifyBtn, email: emailInput } = e(modal, null);

    if (!notifyBtn || !emailInput) return; // Defensive check

    let productData;
    try {
      productData = JSON.parse(productJsonEl.innerHTML);
    } catch (err) {
      console.error("Failed to parse product JSON:", err);
      return;
    }

    const { product: { id: productId } } = productData;
    let modalVariant;

    // Animate in
    setTimeout(() => modal.classList.add("open"), 50);

    app.emit("body:toggleScroll", () => ({ enable: true }));

    // Close button handlers
    if (closeBtn) {
      [].concat(closeBtn).forEach((btn) => {
        btn.addEventListener("click", (ev) => {
          ev.preventDefault();
          modal.classList.remove("open");
          app.emit("body:toggleScroll", () => ({ enable: false }));
        });
      });
    }

    // Notify button handler
    notifyBtn.addEventListener("click", (ev) => {
      ev.preventDefault();

      // Validate email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailInput.value)) {
        emailInput.classList.add("input-error");
        return;
      }

      const variant = modalVariant?.id ? modalVariant : getCurrentVariant();
      if (!variant?.id || !productId) {
        console.error("No variant selected or product ID missing");
        return;
      }

      const compoundVariantId = `$shopify:::$default:::${variant.id}`;

      const requestData = {
        data: {
          type: "back-in-stock-subscription",
          attributes: {
            channels: ["EMAIL"],
            profile: {
              data: {
                type: "profile",
                attributes: { email: emailInput.value },
              },
            },
          },
          relationships: {
            variant: {
              data: {
                type: "catalog-variant",
                id: compoundVariantId,
              },
            },
          },
        },
      };

      const klaviyoPublicKey = "aGSqbu";

      fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${klaviyoPublicKey}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          revision: "2024-10-15",
        },
        body: JSON.stringify(requestData),
      })
        .then((response) => {
          if (response.status === 202 || response.ok) {
            modal.classList.add("success");
          } else {
            return response.json().then((err) => {
              console.error("API Error:", err);
              throw new Error(response.statusText);
            });
          }
        })
        .catch((err) => {
          console.error("Error subscribing:", err);
        });
    });

    // Clear error on input
    emailInput.addEventListener("input", () => {
      emailInput.classList.remove("input-error");
    });
  });

  const sizeSelector = window.component(async (drawer, app) => {
    const { toggle: toggleBtns, focusEl } = e(drawer, null);

    if (!toggleBtns) return; // Defensive check

    app.on("sizeSelector:toggle", ({ sizeSelectorOpen }) => {
      drawer.classList[sizeSelectorOpen ? "add" : "remove"]("open");
      
      if (sizeSelectorOpen) {
        drawer.tabIndex = "0";
        (focusEl || drawer).focus();
        // Emit event to re-initialize sliders when opening
        app.emit("sizeSelector:opened");
      } else {
        drawer.tabIndex = "-1";
      }

      app.emit("body:toggleScroll", () => ({ enable: sizeSelectorOpen ?? false }));
    });

    [].concat(toggleBtns).forEach((btn) => {
      btn.addEventListener("click", () => {
        app.emit("sizeSelector:toggle", ({ sizeSelectorOpen }) => ({
          sizeSelectorOpen: !sizeSelectorOpen ?? true,
        }));
      });
    });

    // Escape key handler
    drawer.addEventListener("keyup", (ev) => {
      if (ev.keyCode === 27) {
        app.emit("sizeSelector:toggle", { sizeSelectorOpen: false });
      }
    });
  });

  // Get variants for size selectors
  let productVariants = [];
  let productMeta = {};
  try {
    const data = JSON.parse(productJsonEl.innerHTML);
    productVariants = data.product?.variants || [];
    productMeta = {
      id: data.product?.id,
      title: data.product?.title,
      handle: data.product?.handle,
      type: data.product?.type,
    };
  } catch (err) {
    console.error("Failed to parse product variants:", err);
  }

  // ==========================================================================
  // SIZE WIZARD TRACKING
  // ==========================================================================

  const storeSizeWizardData = (payload) => {
    const config = window.Stox?.sizeWizardTracking;
    if (!config?.enabled || !config?.endpoint) return;

    const data = {
      timestamp: new Date().toISOString(),
      product_id: productMeta.id,
      product_title: productMeta.title,
      product_handle: productMeta.handle,
      product_type: productMeta.type,
      country: window.Stox?.country || '',
      locale: window.Stox?.locale?.iso_code || '',
      page_url: window.location.href,
      ...payload,
    };

    const body = JSON.stringify(data);

    // Use sendBeacon (preferred for analytics-style tracking), fall back to fetch.
    // text/plain avoids CORS preflight so it works with Google Apps Script endpoints.
    const sent = navigator.sendBeacon?.(
      config.endpoint,
      new Blob([body], { type: 'text/plain' })
    );

    if (!sent) {
      fetch(config.endpoint, {
        method: 'POST',
        mode: 'no-cors',
        body: body,
        keepalive: true,
      }).catch(() => {});
    }
  };

  // ==========================================================================
  // SIZE OPTIONS AVAILABILITY STYLING
  // ==========================================================================

  /**
   * Updates size option styling based on variant availability.
   * Grey out size options when all variants with that size are out of stock.
   */
  const updateSizeOptionsAvailability = () => {
    const sizeRadios = document.querySelectorAll("[data-size-radio]");
    if (!sizeRadios.length || !productVariants.length) return;

    const isUK = window.Stox?.country === 'GB';

    sizeRadios.forEach((radio) => {
      const sizeValue = radio.value;
      const label = radio.nextElementSibling;
      const wrapper = radio.closest('div');

      // Find all variants with this size (could be in option1 or option2)
      const variantsWithSize = productVariants.filter(
        (v) => v.option1 === sizeValue || v.option2 === sizeValue
      );

      // Check if ANY variant with this size is available
      const isAvailable = variantsWithSize.some((v) => {
        // For UK, check inventory directly
        if (isUK) {
          return v.inventory_quantities > 0;
        }
        // For other markets, check the available flag
        return v.available;
      });

      // Apply styling based on availability
      if (isAvailable) {
        // Available - normal styling
        radio.classList.remove("before:border-grey-500", "size-out-of-stock");
        radio.classList.add("before:border-black");
        if (label) {
          label.classList.remove("text-grey-mid", "size-label-unavailable");
        }
      } else {
        // Out of stock - grey styling (still clickable)
        radio.classList.remove("before:border-black");
        radio.classList.add("before:border-grey-500", "size-out-of-stock");
        if (label) {
          label.classList.add("text-grey-mid", "size-label-unavailable");
        }
      }
    });
  };

  // Call on DOM ready
  window.Stox?.onReady?.(() => {
    updateSizeOptionsAvailability();
  });

  const genderedSelector = window.component((container, app) => {
    const { 
      rangeSlider: shoeSliderEl, 
      output: shoeOutputEl, 
      calfSlider: calfSliderEl, 
      calfOutput: calfOutputEl, 
      button: btnEl,
      crossGenderRecommendation: crossGenderEl,
      crossGenderSize: crossGenderSizeEl,
      crossGenderLink: crossGenderLinkEl
    } = e(container, null);

    if (!btnEl) return; // Defensive check

    const { weAdvise: weAdviseText, betweenSizes: betweenSizesText, email: emailHref } = btnEl.dataset;

    let criteriaData;
    try {
      criteriaData = JSON.parse(container.dataset.criteria);
    } catch (err) {
      console.error("Failed to parse gendered criteria:", err);
      return;
    }
    
    // Parse opposite gender criteria for cross-gender recommendations
    let oppositeGenderCriteriaData;
    try {
      oppositeGenderCriteriaData = JSON.parse(container.dataset.oppositeGenderCriteria || '{"criteria":[]}');
    } catch (err) {
      console.warn("Failed to parse opposite gender criteria:", err);
      oppositeGenderCriteriaData = { criteria: [] };
    }
    
    // Get cross-gender URL data
    const oppositeGenderProductUrlGuess = container.dataset.oppositeGenderProductUrlGuess || '';
    const oppositeFamilyName = container.dataset.oppositeFamilyName || '';
    const oppositeGenderCollectionUrl = container.dataset.oppositeGenderCollectionUrl || '';
    const localeRoot = container.dataset.localeRoot || '/';
    
    // Get small sizes data (for M0/W0 unavailable)
    const smallSizesUrl = container.dataset.smallSizesUrl || '';
    const availableSizes = (container.dataset.availableSizes || '').split(',').filter(s => s.trim());
    const smallSizeM0Text = container.dataset.smallSizeM0Text || 'Go to M0 collection';
    const smallSizeW0Text = container.dataset.smallSizeW0Text || 'Go to W0 collection';
    
    // Cache for validated cross-gender URL (validated on first need)
    let validatedCrossGenderUrl = null;
    let crossGenderUrlValidated = false;

    let shoeValue = shoeSliderEl?.value;
    let calfValue = calfSliderEl?.value;
    
    // Function to re-initialize sliders (used on open and pageshow)
    const reinitializeSliders = () => {
      if (shoeSliderEl) {
        shoeValue = shoeSliderEl.value;
        updateSliderTrack(shoeSliderEl);
        if (shoeOutputEl) {
          shoeOutputEl.textContent = shoeSliderEl.value;
        }
      }
      if (calfSliderEl) {
        calfValue = calfSliderEl.value;
        updateSliderTrack(calfSliderEl);
        if (calfOutputEl) {
          calfOutputEl.textContent = formatSliderValue(calfSliderEl.value, calfSliderEl);
        }
      }
      updateButton();
    };
    
    // Re-initialize sliders when size selector is opened
    app.on("sizeSelector:opened", reinitializeSliders);
    
    // Re-initialize on pageshow (handles bfcache)
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        reinitializeSliders();
      }
    });

    // Find matching size based on calf value and shoe size
    // Uses direct range comparison instead of bucket matching to handle overlapping ranges
    const findSize = (calfVal, shoeVal, criteria = criteriaData) => {
      if (!criteria?.criteria) return null;
      const numShoe = parseFloat(shoeVal);
      const numCalf = parseFloat(calfVal);
      for (let i = 0; i < criteria.criteria.length; i++) {
        const c = criteria.criteria[i];
        // Parse calf range from string like "30-34" into min/max values
        const calfParts = c.calf.split('-');
        const minCalf = parseFloat(calfParts[0]);
        const maxCalf = parseFloat(calfParts[1]);
        // Check if both calf and shoe size are within the criteria range
        const calfInRange = numCalf >= minCalf && numCalf <= maxCalf;
        const shoeInRange = numShoe >= parseFloat(c.minShoeSize) && numShoe <= parseFloat(c.maxShoeSize);
        if (calfInRange && shoeInRange) {
          return c.title;
        }
      }
      return null;
    };
    
    // Find size in opposite gender criteria (for cross-gender recommendation)
    const findSizeInOppositeGender = (calfVal, shoeVal) => {
      return findSize(calfVal, shoeVal, oppositeGenderCriteriaData);
    };
    
    // Get the cross-gender URL (validates guessed URL, then tries search API)
    const getCrossGenderUrl = async () => {
      // If already validated, return cached result
      if (crossGenderUrlValidated) {
        return validatedCrossGenderUrl || oppositeGenderCollectionUrl;
      }
      
      // Method 1: Try the handle-based URL guess (exact color match)
      if (oppositeGenderProductUrlGuess) {
        try {
          const response = await fetch(oppositeGenderProductUrlGuess, { method: 'HEAD' });
          if (response.ok) {
            validatedCrossGenderUrl = oppositeGenderProductUrlGuess;
            crossGenderUrlValidated = true;
            return validatedCrossGenderUrl;
          }
        } catch (e) {
          // Fetch failed, try search
        }
      }
      
      // Method 2: Search for a product in the opposite gender family
      if (oppositeFamilyName) {
        try {
          const searchUrl = `/search/suggest.json?q=${encodeURIComponent(oppositeFamilyName)}&resources[type]=product&resources[limit]=1`;
          const searchResponse = await fetch(searchUrl);
          if (searchResponse.ok) {
            const searchData = await searchResponse.json();
            const products = searchData.resources?.results?.products;
            if (products && products.length > 0) {
              let productUrl = products[0].url;
              // Ensure URL is localized - prepend locale root if URL starts with /products/
              if (localeRoot !== '/' && productUrl.startsWith('/products/')) {
                const normalizedRoot = localeRoot.endsWith('/') ? localeRoot : localeRoot + '/';
                productUrl = normalizedRoot + productUrl.substring(1);
              }
              validatedCrossGenderUrl = productUrl;
              crossGenderUrlValidated = true;
              return validatedCrossGenderUrl;
            }
          }
        } catch (e) {
          // Search failed, use collection fallback
        }
      }
      
      crossGenderUrlValidated = true;
      return oppositeGenderCollectionUrl;
    };

    // Update button based on selected values
    const updateButton = async () => {
      const size = findSize(calfValue, shoeValue);
      
      // Hide cross-gender recommendation by default
      if (crossGenderEl) {
        crossGenderEl.classList.add('hidden');
      }
      
      if (size) {
        // Check if size is M0 or W0 and not available - redirect to small sizes collection
        const isSmallSize = size === 'M0' || size === 'W0';
        const sizeAvailable = availableSizes.includes(size);
        
        if (isSmallSize && !sizeAvailable && smallSizesUrl) {
          // Size is M0/W0 but not available - link to small sizes collection
          const buttonText = size === 'M0' ? smallSizeM0Text : smallSizeW0Text;
          btnEl.textContent = buttonText;
          btnEl.dataset.redirectUrl = smallSizesUrl;
          btnEl.classList.remove("bg-white", "text-black", "border-2", "border-black");
          btnEl.classList.add("bg-black", "text-white");
        } else {
          // Normal recommendation
          btnEl.innerHTML = `${weAdviseText}&nbsp;<strong>${size}</strong>`;
          delete btnEl.dataset.redirectUrl;
          btnEl.classList.remove("bg-white", "text-black", "border-2", "border-black");
          btnEl.classList.add("bg-black", "text-white");
        }
      } else {
        // No size found for current gender - check opposite gender
        const oppositeGenderSize = findSizeInOppositeGender(calfValue, shoeValue);
        
        if (oppositeGenderSize && crossGenderEl) {
          // Check if opposite gender size is M0/W0 and not available
          const isSmallSize = oppositeGenderSize === 'M0' || oppositeGenderSize === 'W0';
          const sizeAvailable = availableSizes.includes(oppositeGenderSize);
          
          if (isSmallSize && !sizeAvailable && smallSizesUrl) {
            // Size is M0/W0 but not available - link to small sizes collection
            const buttonText = oppositeGenderSize === 'M0' ? smallSizeM0Text : smallSizeW0Text;
            btnEl.textContent = buttonText;
            btnEl.dataset.redirectUrl = smallSizesUrl;
            btnEl.classList.remove("bg-white", "text-black", "border-2", "border-black");
            btnEl.classList.add("bg-black", "text-white");
          } else {
            // Show cross-gender recommendation with size in main button
            crossGenderEl.classList.remove('hidden');
            
            // Get validated cross-gender URL (async validation only on first need)
            let crossGenderUrl = await getCrossGenderUrl();
            
            // Add size parameter to pre-select variant on target page
            const separator = crossGenderUrl.includes('?') ? '&' : '?';
            crossGenderUrl = `${crossGenderUrl}${separator}size=${encodeURIComponent(oppositeGenderSize)}`;
            
            // Main button shows the recommended size and links to other collection
            btnEl.innerHTML = `${weAdviseText}&nbsp;<strong>${oppositeGenderSize}</strong>`;
            btnEl.dataset.redirectUrl = crossGenderUrl;
            btnEl.classList.remove("bg-white", "text-black", "border-2", "border-black");
            btnEl.classList.add("bg-black", "text-white");
            
            // Also set the link in the recommendation text
            if (crossGenderLinkEl) {
              crossGenderLinkEl.href = crossGenderUrl;
            }
          }
        } else {
          // No size found in either gender
          btnEl.textContent = betweenSizesText;
          btnEl.dataset.redirectUrl = emailHref;
          btnEl.classList.remove("bg-black", "text-white");
          btnEl.classList.add("bg-white", "text-black", "border-2", "border-black");
        }
      }
      return size;
    };

    // Initialize shoe slider
    if (shoeSliderEl) {
      updateSliderTrack(shoeSliderEl);
      if (shoeOutputEl) {
        shoeOutputEl.textContent = shoeSliderEl.value;
      }
      shoeSliderEl.addEventListener("input", (ev) => {
        shoeValue = ev.target.value;
        updateSliderTrack(shoeSliderEl);
        if (shoeOutputEl) {
          shoeOutputEl.textContent = shoeValue;
        }
        updateButton();
      });
      
      // Add click handlers for shoe slider increment/decrement buttons
      const shoeSliderSection = shoeSliderEl.closest('[data-shoe-slider-section]');
      if (shoeSliderSection) {
        const shoeDecrementBtn = shoeSliderSection.querySelector('[data-decrement]');
        const shoeIncrementBtn = shoeSliderSection.querySelector('[data-increment]');
        const step = parseFloat(shoeSliderEl.step) || 1;
        const min = parseFloat(shoeSliderEl.min);
        const max = parseFloat(shoeSliderEl.max);
        
        // Update disabled state of shoe slider buttons
        const updateShoeButtonStates = () => {
          const currentVal = parseFloat(shoeSliderEl.value);
          if (shoeDecrementBtn) {
            const isAtMin = currentVal <= min;
            shoeDecrementBtn.disabled = isAtMin;
            shoeDecrementBtn.classList.toggle('slider-btn-disabled', isAtMin);
          }
          if (shoeIncrementBtn) {
            const isAtMax = currentVal >= max;
            shoeIncrementBtn.disabled = isAtMax;
            shoeIncrementBtn.classList.toggle('slider-btn-disabled', isAtMax);
          }
        };
        
        if (shoeDecrementBtn) {
          shoeDecrementBtn.addEventListener('click', () => {
            const newValue = Math.max(min, parseFloat(shoeSliderEl.value) - step);
            shoeSliderEl.value = newValue;
            shoeValue = newValue;
            updateSliderTrack(shoeSliderEl);
            if (shoeOutputEl) shoeOutputEl.textContent = newValue;
            updateButton();
            updateShoeButtonStates();
          });
        }
        
        if (shoeIncrementBtn) {
          shoeIncrementBtn.addEventListener('click', () => {
            const newValue = Math.min(max, parseFloat(shoeSliderEl.value) + step);
            shoeSliderEl.value = newValue;
            shoeValue = newValue;
            updateSliderTrack(shoeSliderEl);
            if (shoeOutputEl) shoeOutputEl.textContent = newValue;
            updateButton();
            updateShoeButtonStates();
          });
        }
        
        // Also update on slider input
        shoeSliderEl.addEventListener('input', updateShoeButtonStates);
        
        // Initialize button states
        updateShoeButtonStates();
      }
    }

    // Initialize calf slider
    if (calfSliderEl) {
      updateSliderTrack(calfSliderEl);
      if (calfOutputEl) {
        calfOutputEl.textContent = formatSliderValue(calfSliderEl.value, calfSliderEl);
      }
      
      // Add click handlers for calf slider increment/decrement buttons
      const calfSliderSection = calfSliderEl.closest('[data-calf-slider-section]');
      if (calfSliderSection) {
        const calfDecrementBtn = calfSliderSection.querySelector('[data-decrement]');
        const calfIncrementBtn = calfSliderSection.querySelector('[data-increment]');
        const step = parseFloat(calfSliderEl.step) || 1;
        const min = parseFloat(calfSliderEl.min);
        const max = parseFloat(calfSliderEl.max);
        
        // Update disabled state of calf slider buttons
        const updateCalfButtonStates = () => {
          const currentVal = parseFloat(calfSliderEl.value);
          if (calfDecrementBtn) {
            const isAtMin = currentVal <= min;
            calfDecrementBtn.disabled = isAtMin;
            calfDecrementBtn.classList.toggle('slider-btn-disabled', isAtMin);
          }
          if (calfIncrementBtn) {
            const isAtMax = currentVal >= max;
            calfIncrementBtn.disabled = isAtMax;
            calfIncrementBtn.classList.toggle('slider-btn-disabled', isAtMax);
          }
        };
        
        if (calfDecrementBtn) {
          calfDecrementBtn.addEventListener('click', () => {
            const newValue = Math.max(min, parseFloat(calfSliderEl.value) - step);
            calfSliderEl.value = newValue;
            calfValue = newValue;
            updateSliderTrack(calfSliderEl);
            if (calfOutputEl) calfOutputEl.textContent = formatSliderValue(newValue, calfSliderEl);
            updateButton();
            updateCalfButtonStates();
          });
        }
        
        if (calfIncrementBtn) {
          calfIncrementBtn.addEventListener('click', () => {
            const newValue = Math.min(max, parseFloat(calfSliderEl.value) + step);
            calfSliderEl.value = newValue;
            calfValue = newValue;
            updateSliderTrack(calfSliderEl);
            if (calfOutputEl) calfOutputEl.textContent = formatSliderValue(newValue, calfSliderEl);
            updateButton();
            updateCalfButtonStates();
          });
        }
        
        // Initialize button states
        updateCalfButtonStates();
        
        // Update button states on slider input
        calfSliderEl.addEventListener("input", (ev) => {
          calfValue = ev.target.value;
          updateSliderTrack(calfSliderEl);
          if (calfOutputEl) {
            calfOutputEl.textContent = formatSliderValue(ev.target.value, calfSliderEl);
          }
          updateButton();
          updateCalfButtonStates();
        });
      } else {
        // Fallback: still add input listener even without buttons
        calfSliderEl.addEventListener("input", (ev) => {
          calfValue = ev.target.value;
          updateSliderTrack(calfSliderEl);
          if (calfOutputEl) {
            calfOutputEl.textContent = formatSliderValue(ev.target.value, calfSliderEl);
          }
          updateButton();
        });
      }
    }

    updateButton();

    // Apply matched size on button click
    btnEl.addEventListener("click", () => {
      // Check if button has a redirect URL (for small sizes or cross-gender)
      const redirectUrl = btnEl.dataset.redirectUrl;
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }
      
      const size = findSize(calfValue, shoeValue);
      const isCrossGender = !size && !!findSizeInOppositeGender(calfValue, shoeValue);
      const recommendedSize = size || findSizeInOppositeGender(calfValue, shoeValue);

      storeSizeWizardData({
        wizard_type: 'default',
        gender: container.dataset.gender || '',
        recommended_size: recommendedSize || null,
        is_cross_gender: isCrossGender,
        measurement_unit: shoeSliderEl?.dataset?.unit || (parseFloat(shoeSliderEl?.step) === 0.5 ? 'imperial' : 'metric'),
        inputs: {
          shoe_size: parseFloat(shoeValue),
          calf_size: parseFloat(calfValue),
        },
      });

      if (!size) return;
      
      const matchingVariant = productVariants.find((v) => v.option2 === size);
      if (matchingVariant) {
        app.emit("variant:change", null, matchingVariant);
        app.emit("sizeSelector:toggle", ({ sizeSelectorOpen }) => ({
          sizeSelectorOpen: !sizeSelectorOpen ?? true,
        }));
      }
    });
  });

  const unisexSelector = window.component(async (container, app) => {
    const {
      ankleSlider: ankleSliderEl,
      ankleOutput: ankleOutputEl,
      unisexCalfSlider: calfSliderEl,
      unisexCalfOutput: calfOutputEl,
      lowerLegSlider: lowerLegSliderEl,
      lowerLegOutput: lowerLegOutputEl,
      button: btnEl
    } = e(container, null);

    if (!btnEl) return; // Defensive check

    const { weAdvise: weAdviseText, betweenSizes: betweenSizesText, email: emailHref } = btnEl.dataset;

    let criteriaData;
    try {
      criteriaData = JSON.parse(container.dataset.criteria);
    } catch (err) {
      console.error("Failed to parse unisex criteria:", err);
      return;
    }

    let ankleValue = ankleSliderEl?.value;
    let calfValue = calfSliderEl?.value;
    let lowerLegValue = lowerLegSliderEl?.value;

    // Parse a "min-max" range string into [min, max] numbers.
    // Returns [NaN, NaN] if the string is malformed so callers can decide.
    const parseRange = (range) => {
      if (range == null) return [NaN, NaN];
      const parts = String(range).split('-');
      return [parseFloat(parts[0]), parseFloat(parts[1])];
    };

    // Find matching size: ankle, calf, and lower-leg must all fall within
    // the criteria's ranges. Returns the first match in criteria order.
    // Direct numeric range matching is required because unisex calf ranges
    // overlap heavily (e.g. calf 37cm fits S, M, L, XL); a bucket-based
    // approach would always lock onto the first range and ignore the others.
    const findSize = (calfVal, legVal, ankleVal) => {
      if (!criteriaData?.criteria) return null;
      const numCalf = parseFloat(calfVal);
      const numLeg = parseFloat(legVal);
      const numAnkle = parseFloat(ankleVal);
      for (let i = 0; i < criteriaData.criteria.length; i++) {
        const c = criteriaData.criteria[i];
        const [minCalf, maxCalf] = parseRange(c.calf);
        const [minLeg, maxLeg] = parseRange(c.lowerLeg);
        const calfInRange = numCalf >= minCalf && numCalf <= maxCalf;
        const legInRange = numLeg >= minLeg && numLeg <= maxLeg;
        const ankleInRange =
          numAnkle >= parseFloat(c.minAnkleGirth) &&
          numAnkle <= parseFloat(c.maxAnkleGirth);
        if (calfInRange && legInRange && ankleInRange) {
          return c.title;
        }
      }
      return null;
    };

    // Update button based on selected values
    const updateButton = () => {
      const size = findSize(calfValue, lowerLegValue, ankleValue);
      if (size) {
        btnEl.innerHTML = `${weAdviseText}&nbsp;<strong>${size}</strong>`;
        delete btnEl.dataset.redirectUrl;
        btnEl.classList.remove("bg-white", "text-black", "border-2", "border-black");
        btnEl.classList.add("bg-black", "text-white");
      } else {
        btnEl.textContent = betweenSizesText;
        btnEl.dataset.redirectUrl = emailHref;
        btnEl.classList.remove("bg-black", "text-white");
        btnEl.classList.add("bg-white", "text-black", "border-2", "border-black");
      }
      return size;
    };

    // Initialize ankle slider
    if (ankleSliderEl) {
      updateSliderTrack(ankleSliderEl);
      if (ankleOutputEl) {
        ankleOutputEl.textContent = formatSliderValue(ankleSliderEl.value, ankleSliderEl);
      }
      ankleSliderEl.addEventListener("input", (ev) => {
        ankleValue = ev.target.value;
        updateSliderTrack(ankleSliderEl);
        if (ankleOutputEl) {
          ankleOutputEl.textContent = formatSliderValue(ankleValue, ankleSliderEl);
        }
        updateButton();
      });
      
      // Add click handlers for ankle slider increment/decrement buttons
      const ankleSliderSection = ankleSliderEl.closest('[data-ankle-slider-section]');
      if (ankleSliderSection) {
        const decrementBtn = ankleSliderSection.querySelector('[data-decrement]');
        const incrementBtn = ankleSliderSection.querySelector('[data-increment]');
        const step = parseFloat(ankleSliderEl.step) || 1;
        const min = parseFloat(ankleSliderEl.min);
        const max = parseFloat(ankleSliderEl.max);
        
        if (decrementBtn) {
          decrementBtn.addEventListener('click', () => {
            const newValue = Math.max(min, parseFloat(ankleSliderEl.value) - step);
            ankleSliderEl.value = newValue;
            ankleValue = newValue;
            updateSliderTrack(ankleSliderEl);
            if (ankleOutputEl) ankleOutputEl.textContent = formatSliderValue(newValue, ankleSliderEl);
            updateButton();
          });
        }
        
        if (incrementBtn) {
          incrementBtn.addEventListener('click', () => {
            const newValue = Math.min(max, parseFloat(ankleSliderEl.value) + step);
            ankleSliderEl.value = newValue;
            ankleValue = newValue;
            updateSliderTrack(ankleSliderEl);
            if (ankleOutputEl) ankleOutputEl.textContent = formatSliderValue(newValue, ankleSliderEl);
            updateButton();
          });
        }
      }
    }

    // Initialize calf slider
    if (calfSliderEl) {
      updateSliderTrack(calfSliderEl);
      if (calfOutputEl) {
        calfOutputEl.textContent = formatSliderValue(calfSliderEl.value, calfSliderEl);
      }
      calfSliderEl.addEventListener("input", (ev) => {
        calfValue = ev.target.value;
        updateSliderTrack(calfSliderEl);
        if (calfOutputEl) {
          calfOutputEl.textContent = formatSliderValue(ev.target.value, calfSliderEl);
        }
        updateButton();
      });
      
      // Add click handlers for calf slider increment/decrement buttons
      const calfSliderSection = calfSliderEl.closest('[data-unisex-calf-slider-section]');
      if (calfSliderSection) {
        const decrementBtn = calfSliderSection.querySelector('[data-decrement]');
        const incrementBtn = calfSliderSection.querySelector('[data-increment]');
        const step = parseFloat(calfSliderEl.step) || 1;
        const min = parseFloat(calfSliderEl.min);
        const max = parseFloat(calfSliderEl.max);
        
        if (decrementBtn) {
          decrementBtn.addEventListener('click', () => {
            const newValue = Math.max(min, parseFloat(calfSliderEl.value) - step);
            calfSliderEl.value = newValue;
            calfValue = newValue;
            updateSliderTrack(calfSliderEl);
            if (calfOutputEl) calfOutputEl.textContent = formatSliderValue(newValue, calfSliderEl);
            updateButton();
          });
        }
        
        if (incrementBtn) {
          incrementBtn.addEventListener('click', () => {
            const newValue = Math.min(max, parseFloat(calfSliderEl.value) + step);
            calfSliderEl.value = newValue;
            calfValue = newValue;
            updateSliderTrack(calfSliderEl);
            if (calfOutputEl) calfOutputEl.textContent = formatSliderValue(newValue, calfSliderEl);
            updateButton();
          });
        }
      }
    }

    // Initialize lower leg slider
    if (lowerLegSliderEl) {
      updateSliderTrack(lowerLegSliderEl);
      if (lowerLegOutputEl) {
        lowerLegOutputEl.textContent = formatSliderValue(lowerLegSliderEl.value, lowerLegSliderEl);
      }
      lowerLegSliderEl.addEventListener("input", (ev) => {
        lowerLegValue = ev.target.value;
        updateSliderTrack(lowerLegSliderEl);
        if (lowerLegOutputEl) {
          lowerLegOutputEl.textContent = formatSliderValue(ev.target.value, lowerLegSliderEl);
        }
        updateButton();
      });
      
      // Add click handlers for lower leg slider increment/decrement buttons
      const lowerLegSliderSection = lowerLegSliderEl.closest('[data-lower-leg-slider-section]');
      if (lowerLegSliderSection) {
        const decrementBtn = lowerLegSliderSection.querySelector('[data-decrement]');
        const incrementBtn = lowerLegSliderSection.querySelector('[data-increment]');
        const step = parseFloat(lowerLegSliderEl.step) || 1;
        const min = parseFloat(lowerLegSliderEl.min);
        const max = parseFloat(lowerLegSliderEl.max);
        
        if (decrementBtn) {
          decrementBtn.addEventListener('click', () => {
            const newValue = Math.max(min, parseFloat(lowerLegSliderEl.value) - step);
            lowerLegSliderEl.value = newValue;
            lowerLegValue = newValue;
            updateSliderTrack(lowerLegSliderEl);
            if (lowerLegOutputEl) lowerLegOutputEl.textContent = formatSliderValue(newValue, lowerLegSliderEl);
            updateButton();
          });
        }
        
        if (incrementBtn) {
          incrementBtn.addEventListener('click', () => {
            const newValue = Math.min(max, parseFloat(lowerLegSliderEl.value) + step);
            lowerLegSliderEl.value = newValue;
            lowerLegValue = newValue;
            updateSliderTrack(lowerLegSliderEl);
            if (lowerLegOutputEl) lowerLegOutputEl.textContent = formatSliderValue(newValue, lowerLegSliderEl);
            updateButton();
          });
        }
      }
    }

    updateButton();

    // Apply matched size on button click
    btnEl.addEventListener("click", () => {
      // Check if button has a redirect URL (for small sizes or cross-gender)
      const redirectUrl = btnEl.dataset.redirectUrl;
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }
      
      const size = findSize(calfValue, lowerLegValue, ankleValue);

      storeSizeWizardData({
        wizard_type: 'unisex',
        gender: 'unisex',
        recommended_size: size || null,
        is_cross_gender: false,
        measurement_unit: ankleSliderEl?.dataset?.unit || 'metric',
        inputs: {
          ankle_girth: parseFloat(ankleValue),
          calf_size: parseFloat(calfSliderEl?.value),
          lower_leg: parseFloat(lowerLegSliderEl?.value),
        },
      });

      if (!size) return;

      const currentVar = getCurrentVariant();
      const matchingVariant = productVariants.find(
        (v) => v.option1 === currentVar.option1 && v.option2 === size
      );

      if (matchingVariant) {
        app.emit("variant:change", null, matchingVariant);
        app.emit("sizeSelector:toggle", ({ sizeSelectorOpen }) => ({
          sizeSelectorOpen: !sizeSelectorOpen ?? true,
        }));
      }
    });
  });

  // ==========================================================================
  // CALF SLEEVES SIZE SELECTOR (slider-based)
  // ==========================================================================

  /**
   * Helper to format slider value based on unit (metric vs imperial)
   */
  const formatSliderValue = (value, slider) => {
    const isImperial = slider?.dataset?.unit === 'imperial';
    const numVal = parseFloat(value);
    return isImperial ? numVal.toFixed(1) : Math.round(numVal).toString();
  };

  /**
   * Helper to position output element under slider thumb
   */
  const positionSliderOutput = (slider, output) => {
    if (!slider || !output) return;
    const min = parseFloat(slider.min);
    const max = parseFloat(slider.max);
    const val = parseFloat(slider.value);
    const pct = (val - min) / (max - min);
    const thumbWidth = 16;
    const sliderWidth = slider.offsetWidth;
    const usableWidth = sliderWidth - thumbWidth;
    const thumbCenterPx = (thumbWidth / 2) + (pct * usableWidth);
    const pctPosition = (thumbCenterPx / sliderWidth) * 100;
    output.style.position = 'relative';
    output.style.left = `${pctPosition}%`;
    output.style.transform = 'translateX(-50%)';
    output.style.display = 'inline-block';
  };

  /**
   * Helper to update slider track background
   */
  const updateSliderTrack = (slider) => {
    if (!slider) return;
    const pct = ((slider.value - slider.min) / (slider.max - slider.min)) * 100;
    slider.style.backgroundSize = `${pct}% 100%`;
  };

  const calfSleevesSelector = window.component(async (container, app) => {
    const {
      calfSleevesSlider: calfSliderEl,
      calfSleevesOutput: calfOutputEl,
      button: btnEl,
      crossGenderRecommendation: crossGenderEl,
      crossGenderSize: crossGenderSizeEl,
      crossGenderLink: crossGenderLinkEl
    } = e(container, null);

    if (!btnEl) return; // Defensive check

    const { weAdvise: weAdviseText, betweenSizes: betweenSizesText, email: emailHref } = btnEl.dataset;

    let criteriaData;
    try {
      criteriaData = JSON.parse(container.dataset.criteria);
    } catch (err) {
      console.error("Failed to parse calf sleeves criteria:", err);
      return;
    }
    
    // Parse opposite gender criteria for cross-gender recommendations
    let oppositeGenderCriteriaData;
    try {
      oppositeGenderCriteriaData = JSON.parse(container.dataset.oppositeGenderCriteria || '{"criteria":[]}');
    } catch (err) {
      console.warn("Failed to parse opposite gender criteria:", err);
      oppositeGenderCriteriaData = { criteria: [] };
    }
    
    // Get cross-gender URL data
    const oppositeGenderProductUrlGuess = container.dataset.oppositeGenderProductUrlGuess || '';
    const oppositeFamilyName = container.dataset.oppositeFamilyName || '';
    const oppositeGenderCollectionUrl = container.dataset.oppositeGenderCollectionUrl || '';
    const localeRoot = container.dataset.localeRoot || '/';
    
    // Get small sizes data (for M0/W0 unavailable)
    const smallSizesUrl = container.dataset.smallSizesUrl || '';
    const availableSizes = (container.dataset.availableSizes || '').split(',').filter(s => s.trim());
    const smallSizeM0Text = container.dataset.smallSizeM0Text || 'Go to M0 collection';
    const smallSizeW0Text = container.dataset.smallSizeW0Text || 'Go to W0 collection';
    
    // Cache for validated cross-gender URL
    let validatedCrossGenderUrl = null;
    let crossGenderUrlValidated = false;

    let calfValue = calfSliderEl?.value;

    // Update slider background and output
    const updateSlider = (slider, output) => {
      if (!slider) return;
      updateSliderTrack(slider);
      if (output) {
        output.textContent = formatSliderValue(slider.value, slider);
      }
    };
    
    // Function to re-initialize sliders (used on open and pageshow)
    const reinitializeSliders = () => {
      if (calfSliderEl) {
        calfValue = calfSliderEl.value;
        updateSlider(calfSliderEl, calfOutputEl);
      }
      updateButton();
    };
    
    // Re-initialize sliders when size selector is opened
    app.on("sizeSelector:opened", reinitializeSliders);
    
    // Re-initialize on pageshow (handles bfcache)
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        reinitializeSliders();
      }
    });

    // Find matching size based on calf value
    const findSize = (calfVal, criteria = criteriaData) => {
      if (!criteria?.criteria) return null;
      const numVal = parseFloat(calfVal);
      for (let i = 0; i < criteria.criteria.length; i++) {
        const c = criteria.criteria[i];
        if (numVal >= parseFloat(c.minCalf) && numVal <= parseFloat(c.maxCalf)) {
          return c.title;
        }
      }
      return null;
    };
    
    // Find size in opposite gender criteria (for cross-gender recommendation)
    const findSizeInOppositeGender = (calfVal) => {
      return findSize(calfVal, oppositeGenderCriteriaData);
    };
    
    // Get the cross-gender URL (validates guessed URL, then tries search API)
    const getCrossGenderUrl = async () => {
      if (crossGenderUrlValidated) return validatedCrossGenderUrl || oppositeGenderCollectionUrl;
      
      // Method 1: Try the handle-based URL guess
      if (oppositeGenderProductUrlGuess) {
        try {
          const response = await fetch(oppositeGenderProductUrlGuess, { method: 'HEAD' });
          if (response.ok) {
            validatedCrossGenderUrl = oppositeGenderProductUrlGuess;
            crossGenderUrlValidated = true;
            return validatedCrossGenderUrl;
          }
        } catch (e) { /* Fetch failed */ }
      }
      
      // Method 2: Search for a product in the opposite gender family
      if (oppositeFamilyName) {
        try {
          const searchUrl = `/search/suggest.json?q=${encodeURIComponent(oppositeFamilyName)}&resources[type]=product&resources[limit]=1`;
          const searchResponse = await fetch(searchUrl);
          if (searchResponse.ok) {
            const searchData = await searchResponse.json();
            const products = searchData.resources?.results?.products;
            if (products && products.length > 0) {
              let productUrl = products[0].url;
              // Ensure URL is localized - prepend locale root if URL starts with /products/
              if (localeRoot !== '/' && productUrl.startsWith('/products/')) {
                const normalizedRoot = localeRoot.endsWith('/') ? localeRoot : localeRoot + '/';
                productUrl = normalizedRoot + productUrl.substring(1);
              }
              validatedCrossGenderUrl = productUrl;
              crossGenderUrlValidated = true;
              return validatedCrossGenderUrl;
            }
          }
        } catch (e) { /* Search failed */ }
      }
      
      crossGenderUrlValidated = true;
      return oppositeGenderCollectionUrl;
    };

    // Update button based on selected values
    const updateButton = async () => {
      const size = findSize(calfValue);
      
      // Hide cross-gender recommendation by default
      if (crossGenderEl) {
        crossGenderEl.classList.add('hidden');
      }
      
      if (size) {
        // Check if size is M0 or W0 and not available - redirect to small sizes collection
        const isSmallSize = size === 'M0' || size === 'W0';
        const sizeAvailable = availableSizes.includes(size);
        
        if (isSmallSize && !sizeAvailable && smallSizesUrl) {
          const buttonText = size === 'M0' ? smallSizeM0Text : smallSizeW0Text;
          btnEl.textContent = buttonText;
          btnEl.dataset.redirectUrl = smallSizesUrl;
          btnEl.classList.remove("bg-white", "text-black", "border-2", "border-black");
          btnEl.classList.add("bg-black", "text-white");
        } else {
          btnEl.innerHTML = `${weAdviseText}&nbsp;<strong>${size}</strong>`;
          delete btnEl.dataset.redirectUrl;
          btnEl.classList.remove("bg-white", "text-black", "border-2", "border-black");
          btnEl.classList.add("bg-black", "text-white");
        }
      } else {
        // No size found for current gender - check opposite gender
        const oppositeGenderSize = findSizeInOppositeGender(calfValue);
        
        if (oppositeGenderSize && crossGenderEl) {
          // Check if opposite gender size is M0/W0 and not available
          const isSmallSize = oppositeGenderSize === 'M0' || oppositeGenderSize === 'W0';
          const sizeAvailable = availableSizes.includes(oppositeGenderSize);
          
          if (isSmallSize && !sizeAvailable && smallSizesUrl) {
            const buttonText = oppositeGenderSize === 'M0' ? smallSizeM0Text : smallSizeW0Text;
            btnEl.textContent = buttonText;
            btnEl.dataset.redirectUrl = smallSizesUrl;
            btnEl.classList.remove("bg-white", "text-black", "border-2", "border-black");
            btnEl.classList.add("bg-black", "text-white");
          } else {
            // Show cross-gender recommendation with size in main button
            crossGenderEl.classList.remove('hidden');
            
            let crossGenderUrl = await getCrossGenderUrl();
            
            // Add size parameter to pre-select variant on target page
            const separator = crossGenderUrl.includes('?') ? '&' : '?';
            crossGenderUrl = `${crossGenderUrl}${separator}size=${encodeURIComponent(oppositeGenderSize)}`;
            
            btnEl.innerHTML = `${weAdviseText}&nbsp;<strong>${oppositeGenderSize}</strong>`;
            btnEl.dataset.redirectUrl = crossGenderUrl;
            btnEl.classList.remove("bg-white", "text-black", "border-2", "border-black");
            btnEl.classList.add("bg-black", "text-white");
            
            // Also set the link in the recommendation text
            if (crossGenderLinkEl) {
              crossGenderLinkEl.href = crossGenderUrl;
            }
          }
        } else {
          // No size found in either gender
          btnEl.textContent = betweenSizesText;
          btnEl.dataset.redirectUrl = emailHref;
          btnEl.classList.remove("bg-black", "text-white");
          btnEl.classList.add("bg-white", "text-black", "border-2", "border-black");
        }
      }
      return size;
    };

    // Initialize
    if (calfSliderEl) {
      updateSlider(calfSliderEl, calfOutputEl);
      
      // Add click handlers for calf sleeves slider increment/decrement buttons
      const calfSleevesSliderSection = calfSliderEl.closest('[data-calf-sleeves-slider-section]');
      if (calfSleevesSliderSection) {
        const sleevesDecrementBtn = calfSleevesSliderSection.querySelector('[data-decrement]');
        const sleevesIncrementBtn = calfSleevesSliderSection.querySelector('[data-increment]');
        const step = parseFloat(calfSliderEl.step) || 1;
        const min = parseFloat(calfSliderEl.min);
        const max = parseFloat(calfSliderEl.max);
        
        // Update disabled state of calf sleeves slider buttons
        const updateSleevesButtonStates = () => {
          const currentVal = parseFloat(calfSliderEl.value);
          if (sleevesDecrementBtn) {
            const isAtMin = currentVal <= min;
            sleevesDecrementBtn.disabled = isAtMin;
            sleevesDecrementBtn.classList.toggle('slider-btn-disabled', isAtMin);
          }
          if (sleevesIncrementBtn) {
            const isAtMax = currentVal >= max;
            sleevesIncrementBtn.disabled = isAtMax;
            sleevesIncrementBtn.classList.toggle('slider-btn-disabled', isAtMax);
          }
        };
        
        if (sleevesDecrementBtn) {
          sleevesDecrementBtn.addEventListener('click', () => {
            const newValue = Math.max(min, parseFloat(calfSliderEl.value) - step);
            calfSliderEl.value = newValue;
            calfValue = newValue;
            updateSlider(calfSliderEl, calfOutputEl);
            updateButton();
            updateSleevesButtonStates();
          });
        }
        
        if (sleevesIncrementBtn) {
          sleevesIncrementBtn.addEventListener('click', () => {
            const newValue = Math.min(max, parseFloat(calfSliderEl.value) + step);
            calfSliderEl.value = newValue;
            calfValue = newValue;
            updateSlider(calfSliderEl, calfOutputEl);
            updateButton();
            updateSleevesButtonStates();
          });
        }
        
        // Initialize button states
        updateSleevesButtonStates();
        
        // Update button states on slider input
        calfSliderEl.addEventListener("input", (ev) => {
          calfValue = ev.target.value;
          updateSlider(calfSliderEl, calfOutputEl);
          updateButton();
          updateSleevesButtonStates();
        });
      } else {
        // Fallback: still add input listener even without buttons
        calfSliderEl.addEventListener("input", (ev) => {
          calfValue = ev.target.value;
          updateSlider(calfSliderEl, calfOutputEl);
          updateButton();
        });
      }
    }

    updateButton();

    btnEl.addEventListener("click", () => {
      // Check if button has a redirect URL (for small sizes or cross-gender)
      const redirectUrl = btnEl.dataset.redirectUrl;
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }
      
      const size = findSize(calfValue);
      const isCrossGender = !size && !!findSizeInOppositeGender(calfValue);
      const recommendedSize = size || findSizeInOppositeGender(calfValue);

      storeSizeWizardData({
        wizard_type: 'calf-sleeves',
        gender: container.dataset.gender || '',
        recommended_size: recommendedSize || null,
        is_cross_gender: isCrossGender,
        measurement_unit: calfSliderEl?.dataset?.unit || 'metric',
        inputs: {
          calf_size: parseFloat(calfValue),
        },
      });

      if (!size) return;

      const matchingVariant = productVariants.find((v) => v.option2 === size);
      if (matchingVariant) {
        app.emit("variant:change", null, matchingVariant);
        app.emit("sizeSelector:toggle", ({ sizeSelectorOpen }) => ({
          sizeSelectorOpen: !sizeSelectorOpen ?? true,
        }));
      }
    });
  });

  // ==========================================================================
  // ANKLE SOCKS SIZE SELECTOR (slider-based)
  // ==========================================================================

  const ankleSocksSelector = window.component(async (container, app) => {
    const {
      ankleSocksShoeSlider: shoeSliderEl,
      ankleSocksShoeOutput: shoeOutputEl,
      ankleSocksAnkleSlider: ankleSliderEl,
      ankleSocksAnkleOutput: ankleOutputEl,
      button: btnEl,
      crossGenderRecommendation: crossGenderEl,
      crossGenderSize: crossGenderSizeEl,
      crossGenderLink: crossGenderLinkEl
    } = e(container, null);

    if (!btnEl) return; // Defensive check

    const { weAdvise: weAdviseText, betweenSizes: betweenSizesText, email: emailHref } = btnEl.dataset;

    let criteriaData;
    try {
      criteriaData = JSON.parse(container.dataset.criteria);
    } catch (err) {
      console.error("Failed to parse ankle socks criteria:", err);
      return;
    }
    
    // Parse opposite gender criteria for cross-gender recommendations
    let oppositeGenderCriteriaData;
    try {
      oppositeGenderCriteriaData = JSON.parse(container.dataset.oppositeGenderCriteria || '{"criteria":[]}');
    } catch (err) {
      console.warn("Failed to parse opposite gender criteria:", err);
      oppositeGenderCriteriaData = { criteria: [] };
    }
    
    // Get cross-gender URL data
    const oppositeGenderProductUrlGuess = container.dataset.oppositeGenderProductUrlGuess || '';
    const oppositeFamilyName = container.dataset.oppositeFamilyName || '';
    const oppositeGenderCollectionUrl = container.dataset.oppositeGenderCollectionUrl || '';
    const localeRoot = container.dataset.localeRoot || '/';
    
    // Get small sizes data (for M0/W0 unavailable)
    const smallSizesUrl = container.dataset.smallSizesUrl || '';
    const availableSizes = (container.dataset.availableSizes || '').split(',').filter(s => s.trim());
    const smallSizeM0Text = container.dataset.smallSizeM0Text || 'Go to M0 collection';
    const smallSizeW0Text = container.dataset.smallSizeW0Text || 'Go to W0 collection';
    
    // Cache for validated cross-gender URL
    let validatedCrossGenderUrl = null;
    let crossGenderUrlValidated = false;

    let shoeValue = shoeSliderEl?.value;
    let ankleValue = ankleSliderEl?.value;

    // Update slider background and output
    const updateSlider = (slider, output) => {
      if (!slider) return;
      updateSliderTrack(slider);
      if (output) {
        output.textContent = formatSliderValue(slider.value, slider);
      }
    };
    
    // Function to re-initialize sliders (used on open and pageshow)
    const reinitializeSliders = () => {
      if (shoeSliderEl) {
        shoeValue = shoeSliderEl.value;
        updateSlider(shoeSliderEl, shoeOutputEl);
      }
      if (ankleSliderEl) {
        ankleValue = ankleSliderEl.value;
        updateSlider(ankleSliderEl, ankleOutputEl);
      }
      updateButton();
    };
    
    // Re-initialize sliders when size selector is opened
    app.on("sizeSelector:opened", reinitializeSliders);
    
    // Re-initialize on pageshow (handles bfcache)
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        reinitializeSliders();
      }
    });

    // Find matching size based on shoe and ankle values
    const findSize = (shoeVal, ankleVal, criteria = criteriaData) => {
      if (!criteria?.criteria) return null;
      const numShoe = parseFloat(shoeVal);
      const numAnkle = parseFloat(ankleVal);
      for (let i = 0; i < criteria.criteria.length; i++) {
        const c = criteria.criteria[i];
        const shoeInRange = numShoe >= parseFloat(c.minShoeSize) && numShoe <= parseFloat(c.maxShoeSize);
        const ankleInRange = numAnkle >= parseFloat(c.minAnkleGirth) && numAnkle <= parseFloat(c.maxAnkleGirth);
        if (shoeInRange && ankleInRange) {
          return c.title;
        }
      }
      return null;
    };
    
    // Find size in opposite gender criteria (for cross-gender recommendation)
    const findSizeInOppositeGender = (shoeVal, ankleVal) => {
      return findSize(shoeVal, ankleVal, oppositeGenderCriteriaData);
    };
    
    // Get the cross-gender URL (validates guessed URL, then tries search API)
    const getCrossGenderUrl = async () => {
      if (crossGenderUrlValidated) return validatedCrossGenderUrl || oppositeGenderCollectionUrl;
      
      // Method 1: Try the handle-based URL guess
      if (oppositeGenderProductUrlGuess) {
        try {
          const response = await fetch(oppositeGenderProductUrlGuess, { method: 'HEAD' });
          if (response.ok) {
            validatedCrossGenderUrl = oppositeGenderProductUrlGuess;
            crossGenderUrlValidated = true;
            return validatedCrossGenderUrl;
          }
        } catch (e) { /* Fetch failed */ }
      }
      
      // Method 2: Search for a product in the opposite gender family
      if (oppositeFamilyName) {
        try {
          const searchUrl = `/search/suggest.json?q=${encodeURIComponent(oppositeFamilyName)}&resources[type]=product&resources[limit]=1`;
          const searchResponse = await fetch(searchUrl);
          if (searchResponse.ok) {
            const searchData = await searchResponse.json();
            const products = searchData.resources?.results?.products;
            if (products && products.length > 0) {
              let productUrl = products[0].url;
              // Ensure URL is localized - prepend locale root if URL starts with /products/
              if (localeRoot !== '/' && productUrl.startsWith('/products/')) {
                const normalizedRoot = localeRoot.endsWith('/') ? localeRoot : localeRoot + '/';
                productUrl = normalizedRoot + productUrl.substring(1);
              }
              validatedCrossGenderUrl = productUrl;
              crossGenderUrlValidated = true;
              return validatedCrossGenderUrl;
            }
          }
        } catch (e) { /* Search failed */ }
      }
      
      crossGenderUrlValidated = true;
      return oppositeGenderCollectionUrl;
    };

    // Update button based on selected values
    const updateButton = async () => {
      const size = findSize(shoeValue, ankleValue);
      
      // Hide cross-gender recommendation by default
      if (crossGenderEl) {
        crossGenderEl.classList.add('hidden');
      }
      
      if (size) {
        // Check if size is M0 or W0 and not available - redirect to small sizes collection
        const isSmallSize = size === 'M0' || size === 'W0';
        const sizeAvailable = availableSizes.includes(size);
        
        if (isSmallSize && !sizeAvailable && smallSizesUrl) {
          const buttonText = size === 'M0' ? smallSizeM0Text : smallSizeW0Text;
          btnEl.textContent = buttonText;
          btnEl.dataset.redirectUrl = smallSizesUrl;
          btnEl.classList.remove("bg-white", "text-black", "border-2", "border-black");
          btnEl.classList.add("bg-black", "text-white");
        } else {
          btnEl.innerHTML = `${weAdviseText}&nbsp;<strong>${size}</strong>`;
          delete btnEl.dataset.redirectUrl;
          btnEl.classList.remove("bg-white", "text-black", "border-2", "border-black");
          btnEl.classList.add("bg-black", "text-white");
        }
      } else {
        // No size found for current gender - check opposite gender
        const oppositeGenderSize = findSizeInOppositeGender(shoeValue, ankleValue);
        
        if (oppositeGenderSize && crossGenderEl) {
          // Check if opposite gender size is M0/W0 and not available
          const isSmallSize = oppositeGenderSize === 'M0' || oppositeGenderSize === 'W0';
          const sizeAvailable = availableSizes.includes(oppositeGenderSize);
          
          if (isSmallSize && !sizeAvailable && smallSizesUrl) {
            const buttonText = oppositeGenderSize === 'M0' ? smallSizeM0Text : smallSizeW0Text;
            btnEl.textContent = buttonText;
            btnEl.dataset.redirectUrl = smallSizesUrl;
            btnEl.classList.remove("bg-white", "text-black", "border-2", "border-black");
            btnEl.classList.add("bg-black", "text-white");
          } else {
            // Show cross-gender recommendation with size in main button
            crossGenderEl.classList.remove('hidden');
            
            let crossGenderUrl = await getCrossGenderUrl();
            
            // Add size parameter to pre-select variant on target page
            const separator = crossGenderUrl.includes('?') ? '&' : '?';
            crossGenderUrl = `${crossGenderUrl}${separator}size=${encodeURIComponent(oppositeGenderSize)}`;
            
            btnEl.innerHTML = `${weAdviseText}&nbsp;<strong>${oppositeGenderSize}</strong>`;
            btnEl.dataset.redirectUrl = crossGenderUrl;
            btnEl.classList.remove("bg-white", "text-black", "border-2", "border-black");
            btnEl.classList.add("bg-black", "text-white");
            
            // Also set the link in the recommendation text
            if (crossGenderLinkEl) {
              crossGenderLinkEl.href = crossGenderUrl;
            }
          }
        } else {
          // No size found in either gender
          btnEl.textContent = betweenSizesText;
          btnEl.dataset.redirectUrl = emailHref;
          btnEl.classList.remove("bg-black", "text-white");
          btnEl.classList.add("bg-white", "text-black", "border-2", "border-black");
        }
      }
      return size;
    };

    // Initialize shoe slider
    if (shoeSliderEl) {
      updateSlider(shoeSliderEl, shoeOutputEl);
      shoeSliderEl.addEventListener("input", (ev) => {
        shoeValue = ev.target.value;
        updateSlider(shoeSliderEl, shoeOutputEl);
        updateButton();
      });
      
      // Add click handlers for ankle socks shoe slider increment/decrement buttons
      const ankleSocksShoeSection = shoeSliderEl.closest('[data-ankle-socks-shoe-slider-section]');
      if (ankleSocksShoeSection) {
        const decrementBtn = ankleSocksShoeSection.querySelector('[data-decrement]');
        const incrementBtn = ankleSocksShoeSection.querySelector('[data-increment]');
        const step = parseFloat(shoeSliderEl.step) || 1;
        const min = parseFloat(shoeSliderEl.min);
        const max = parseFloat(shoeSliderEl.max);
        
        if (decrementBtn) {
          decrementBtn.addEventListener('click', () => {
            const newValue = Math.max(min, parseFloat(shoeSliderEl.value) - step);
            shoeSliderEl.value = newValue;
            shoeValue = newValue;
            updateSlider(shoeSliderEl, shoeOutputEl);
            updateButton();
          });
        }
        
        if (incrementBtn) {
          incrementBtn.addEventListener('click', () => {
            const newValue = Math.min(max, parseFloat(shoeSliderEl.value) + step);
            shoeSliderEl.value = newValue;
            shoeValue = newValue;
            updateSlider(shoeSliderEl, shoeOutputEl);
            updateButton();
          });
        }
      }
    }

    // Initialize ankle slider
    if (ankleSliderEl) {
      updateSlider(ankleSliderEl, ankleOutputEl);
      ankleSliderEl.addEventListener("input", (ev) => {
        ankleValue = ev.target.value;
        updateSlider(ankleSliderEl, ankleOutputEl);
        updateButton();
      });
      
      // Add click handlers for ankle socks ankle slider increment/decrement buttons
      const ankleSocksAnkleSection = ankleSliderEl.closest('[data-ankle-socks-ankle-slider-section]');
      if (ankleSocksAnkleSection) {
        const decrementBtn = ankleSocksAnkleSection.querySelector('[data-decrement]');
        const incrementBtn = ankleSocksAnkleSection.querySelector('[data-increment]');
        const step = parseFloat(ankleSliderEl.step) || 1;
        const min = parseFloat(ankleSliderEl.min);
        const max = parseFloat(ankleSliderEl.max);
        
        if (decrementBtn) {
          decrementBtn.addEventListener('click', () => {
            const newValue = Math.max(min, parseFloat(ankleSliderEl.value) - step);
            ankleSliderEl.value = newValue;
            ankleValue = newValue;
            updateSlider(ankleSliderEl, ankleOutputEl);
            updateButton();
          });
        }
        
        if (incrementBtn) {
          incrementBtn.addEventListener('click', () => {
            const newValue = Math.min(max, parseFloat(ankleSliderEl.value) + step);
            ankleSliderEl.value = newValue;
            ankleValue = newValue;
            updateSlider(ankleSliderEl, ankleOutputEl);
            updateButton();
          });
        }
      }
    }

    updateButton();

    btnEl.addEventListener("click", () => {
      // Check if button has a redirect URL (for small sizes or cross-gender)
      const redirectUrl = btnEl.dataset.redirectUrl;
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }
      
      const size = findSize(shoeValue, ankleValue);
      const isCrossGender = !size && !!findSizeInOppositeGender(shoeValue, ankleValue);
      const recommendedSize = size || findSizeInOppositeGender(shoeValue, ankleValue);

      storeSizeWizardData({
        wizard_type: 'ankle-socks',
        gender: container.dataset.gender || '',
        recommended_size: recommendedSize || null,
        is_cross_gender: isCrossGender,
        measurement_unit: shoeSliderEl?.dataset?.unit || (parseFloat(shoeSliderEl?.step) === 0.5 ? 'imperial' : 'metric'),
        inputs: {
          shoe_size: parseFloat(shoeValue),
          ankle_girth: parseFloat(ankleValue),
        },
      });

      if (!size) return;

      const matchingVariant = productVariants.find((v) => v.option2 === size);
      if (matchingVariant) {
        app.emit("variant:change", null, matchingVariant);
        app.emit("sizeSelector:toggle", ({ sizeSelectorOpen }) => ({
          sizeSelectorOpen: !sizeSelectorOpen ?? true,
        }));
      }
    });
  });

  // ==========================================================================
  // REGISTER COMPONENTS
  // ==========================================================================

  const productComponents = {
    productSelection,
    productForm,
    mainProduct,
    productBackInStock,
    sizeSelector,
    genderedSelector,
    unisexSelector,
    calfSleevesSelector,
    ankleSocksSelector,
  };

  window.app?.add?.(productComponents);
  window.app?.mount?.();

  // ==========================================================================
  // STANDALONE INITIALIZERS (idempotent)
  // ==========================================================================

  // Use initialization flag to prevent duplicate runs
  if (!window.__productJsInitialized) {
    window.__productJsInitialized = true;

    // --------------------------------------------------------------------------
    // Product Family Color Links
    // --------------------------------------------------------------------------
    const productFamilyJsonEl = document.querySelector("#product-family-json");
    
    if (productFamilyJsonEl) {
      let productFamilyData = [];
      
      try {
        productFamilyData = JSON.parse(productFamilyJsonEl.textContent);
      } catch (err) {
        console.error("Failed to parse product family JSON:", err);
      }

      const updateColorLinksWithSize = (selectedSize) => {
        if (!selectedSize) return;

        document.querySelectorAll(".variant__link").forEach((link) => {
          const span = link.querySelector("span");
          const linkedProductId = span?.id;
          if (!linkedProductId) return;

          const linkedProduct = productFamilyData.find((p) => String(p.id) === String(linkedProductId));
          if (!linkedProduct) return;

          const matchingVariant = linkedProduct.variants?.find((v) => v.option2 === selectedSize);
          if (matchingVariant) {
            const url = new URL(link.href, window.location.origin);
            url.searchParams.set("variant", matchingVariant.id);
            link.setAttribute("href", url.toString());
          }
        });
      };

      // Listen for size changes
      document.querySelectorAll("[data-size-radio]").forEach((radio) => {
        radio.addEventListener("change", (ev) => {
          updateColorLinksWithSize(ev.target.value);
        });
      });

      // Initial update
      window.Stox?.onReady?.(() => {
        const initialSize = document.querySelector("[data-size-radio]:checked");
        if (initialSize) updateColorLinksWithSize(initialSize.value);
      });
    }

    // --------------------------------------------------------------------------
    // Floating Add to Cart Button (Mobile)
    // --------------------------------------------------------------------------
    window.Stox?.onReady?.(() => {
      if (window.innerWidth >= 1024) return; // Only on mobile

      const atcButton = document.getElementById("atc");
      const floatingAtc = document.getElementById("floating-atc");
      const header = document.querySelector("header");
      const slider = document.getElementById("product-image-slider");

      if (!atcButton || !floatingAtc) return;

      const headerHeight = header?.offsetHeight || 0;
      const sliderHeight = slider?.offsetHeight || 0;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            floatingAtc.style.display = entry.isIntersecting ? "none" : "block";
          });
        },
        { threshold: 0 }
      );

      observer.observe(atcButton);

      const floatingBtn = floatingAtc.querySelector("button");
      if (floatingBtn) {
        floatingBtn.addEventListener("click", () => {
          const targetPosition = atcButton.getBoundingClientRect().top + window.scrollY - headerHeight - sliderHeight + 30;
          window.scrollTo({ top: targetPosition, behavior: "smooth" });
        });
      }
    });

    // --------------------------------------------------------------------------
    // PDP Scroll Container for Swatches
    // --------------------------------------------------------------------------
    window.Stox?.onReady?.(() => {
      document.querySelectorAll(".scroll-container-wrapper").forEach((wrapper) => {
        const container = wrapper.querySelector(".scroll-container-product");
        const scrollWrapper = container?.querySelector(".image_swatches");

        if (!container || !scrollWrapper) return;

        const isVisible = () => container.clientWidth > 0;
        const isOverflowing = () => isVisible() && scrollWrapper.scrollWidth > container.clientWidth;

        const updateVisibility = () => {
          const leftBtn = wrapper.querySelector(".scroll-left");
          const rightBtn = wrapper.querySelector(".scroll-right");

          if (!leftBtn || !rightBtn) return;

          if (!isOverflowing()) {
            leftBtn.style.display = "none";
            rightBtn.style.display = "none";
            return;
          }

          const maxScroll = container.scrollWidth - container.clientWidth;
          leftBtn.style.display = container.scrollLeft > 0 ? "block" : "none";
          rightBtn.style.display = container.scrollLeft < maxScroll ? "block" : "none";
        };

        const scrollBy = (dir) => {
          container.scrollBy({ left: dir * 100, behavior: "smooth" });
          setTimeout(updateVisibility, 300);
        };

        const getActiveSwatchLink = () =>
          Array.from(wrapper.querySelectorAll(".variant__link")).find((link) =>
            link.querySelector("span")?.classList.contains("before:border-black")
          );

        const scrollActiveSwatchIntoView = (behavior = "instant") => {
          if (!isVisible() || !isOverflowing()) return;

          const activeLink = getActiveSwatchLink();
          if (!activeLink) return;

          const containerRect = container.getBoundingClientRect();
          const linkRect = activeLink.getBoundingClientRect();
          let newScrollLeft = container.scrollLeft;

          if (linkRect.left < containerRect.left) {
            newScrollLeft += linkRect.left - containerRect.left;
          } else if (linkRect.right > containerRect.right) {
            newScrollLeft += linkRect.right - containerRect.right;
          } else {
            return;
          }

          container.scrollTo({ left: newScrollLeft, behavior });
          requestAnimationFrame(updateVisibility);
        };

        // Swatch link touch handling
        wrapper.querySelectorAll(".variant__link").forEach((link) => {
          let isScrolling = false;
          let touchStartX = 0;

          link.addEventListener("touchstart", (ev) => {
            touchStartX = ev.changedTouches[0].pageX;
            isScrolling = false;
          }, { passive: true });

          link.addEventListener("touchmove", (ev) => {
            const distance = Math.abs(ev.changedTouches[0].pageX - touchStartX);
            if (distance > 10) isScrolling = true;
          }, { passive: true });

          link.addEventListener("touchend", function() {
            if (!isScrolling) {
              saveScrollBeforeColorChange();
              window.location.href = this.getAttribute("href");
            }
          });

          link.addEventListener("click", function(ev) {
            if (isScrolling) {
              ev.preventDefault();
              return;
            }
            saveScrollBeforeColorChange();
          });
        });

        // Scroll buttons
        const leftBtn = wrapper.querySelector(".scroll-left");
        const rightBtn = wrapper.querySelector(".scroll-right");

        if (leftBtn) leftBtn.addEventListener("click", () => scrollBy(-1));
        if (rightBtn) rightBtn.addEventListener("click", () => scrollBy(1));
        
        container.addEventListener("scroll", () => requestAnimationFrame(updateVisibility), { passive: true });

        const revealActiveSwatch = () => scrollActiveSwatchIntoView("instant");

        requestAnimationFrame(revealActiveSwatch);

        if (typeof ResizeObserver !== "undefined") {
          const resizeObserver = new ResizeObserver(() => revealActiveSwatch());
          resizeObserver.observe(scrollWrapper);
        } else {
          window.addEventListener("resize", revealActiveSwatch);
        }

        updateVisibility();
      });
    });
  }
})();
